/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DataTransferMappingStatus, type GroupMode } from '@g17eco/types/data-transfer';
import type { DateRangeType } from '@g17eco/types/common';
import { DATE, formatDateNonUTC, TimeFrameType } from '@utils/date';
import type { Filters } from './data-transfer-utils';

export interface QueryParams {
  childInitiativeId: string;
  startDate: string;
  endDate: string;
  timeRange: string;
  groupMode: GroupMode;
  search: string;
  connection: '' | 'connected' | 'missing';
  survey: '' | 'value' | 'missing';
  status: '' | DataTransferMappingStatus | 'verified';
  validation: '' | 'errors' | 'warnings' | 'notes' | 'partner_notes' | 'discrepancy' | 'outside_tolerance' | 'both_differ' | 'within_tolerance' | 'excluded' | 'clean';
}

const getParam = (params: URLSearchParams, key: keyof QueryParams): string => params.get(key) ?? '';

export const useDataTransferParams = () => {
  const location = useLocation();
  const history = useHistory();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const childInitiativeId = getParam(params, 'childInitiativeId');
  const startDate = getParam(params, 'startDate');
  const endDate = getParam(params, 'endDate');
  const timeRange = getParam(params, 'timeRange') || String(TimeFrameType.Custom);
  const groupMode = (getParam(params, 'groupMode') || 'survey') as QueryParams['groupMode'];
  const search = getParam(params, 'search');

  const connection = getParam(params, 'connection') as QueryParams['connection'];
  const survey = getParam(params, 'survey') as QueryParams['survey'];
  const status = getParam(params, 'status') as QueryParams['status'];
  const validation = getParam(params, 'validation') as QueryParams['validation'];

  const filters: Filters = useMemo(
    () => ({ connection, survey, status, validation }),
    [connection, survey, status, validation],
  );

  const dateRange: DateRangeType = useMemo(() => {
    const parseLocalDate = (str: string): Date | undefined => {
      if (!str) {
        return undefined;
      }
      const [year, month, day] = str.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    return { startDate: parseLocalDate(startDate), endDate: parseLocalDate(endDate) };
  }, [startDate, endDate]);

  const updateParams = useCallback((updates: Partial<Record<keyof QueryParams, string>>) => {
    const next = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    }
    history.replace({ search: next.toString() });
  }, [history, location.search]);

  const setChildInitiativeId = useCallback((value: string) => {
    updateParams({ childInitiativeId: value });
  }, [updateParams]);

  const setDateRange = useCallback((newDateRange: DateRangeType, period: string | number) => {
    updateParams({
      startDate: newDateRange.startDate ? formatDateNonUTC(newDateRange.startDate, DATE.DATE_PICKER) : '',
      endDate: newDateRange.endDate ? formatDateNonUTC(newDateRange.endDate, DATE.DATE_PICKER) : '',
      timeRange: String(period),
    });
  }, [updateParams]);

  const setFilter = useCallback((key: keyof Filters, value: string) => {
    updateParams({ [key]: value });
  }, [updateParams]);

  const setSearch = useCallback((value: string) => {
    updateParams({ search: value });
  }, [updateParams]);

  const clearFilters = useCallback(() => {
    updateParams({ search: '', connection: '', survey: '', status: '', validation: '' });
  }, [updateParams]);

  const setGroupMode = useCallback((mode: QueryParams['groupMode']) => {
    updateParams({ groupMode: mode });
  }, [updateParams]);

  return { childInitiativeId, startDate, endDate, dateRange, timeRange, groupMode, search, filters, setChildInitiativeId, setDateRange, setGroupMode, setSearch, setFilter, clearFilters };
};
